import {
  assertUnreachable,
  assertUnreachableOrReturnDefault,
} from 'src/lib/assertUnreachable';

export type TypeOfChange =
  | 'ADDITION'
  | 'REMOVAL'
  | 'DEPRECATION'
  | 'SOMETHING_INSIDE_THIS_CHANGED'
  | 'THIS_CHANGED';

// reference: https://github.com/mdg-private/monorepo/blob/651b7eda88d63944fc35efe00e700c9d8ec3fb9c/registry/src/diff/types.ts
export type TypeOfThingThatCanChange = 'field' | 'type' | 'argument';
export function getTypeOfChangeByChangeCodeAndTypeOfThing(
  changeCode: string,
  typeOfThing: TypeOfThingThatCanChange,
): TypeOfChange | null {
  const trimmedChangeCode = changeCode.trim();
  switch (typeOfThing) {
    case 'type': {
      switch (trimmedChangeCode) {
        case 'TYPE_ADDED':
          return 'ADDITION';
        case 'TYPE_REMOVED':
          return 'REMOVAL';
        case 'ENUM_DEPRECATED':
          return 'DEPRECATION';
        case 'ENUM_VALUE_ADDED':
        case 'INTERFACE_ADDED_TO_OBJECT':
        case 'ENUM_VALUE_REMOVED':
        case 'INTERFACE_REMOVED_FROM_OBJECT':
        case 'TYPE_CHANGED_KIND':
        case 'ENUM_DEPRECATION_REMOVED':
        case 'ENUM_DEPRECATED_REASON_CHANGE':
        case 'TYPE_ADDED_TO_UNION':
        case 'TYPE_REMOVED_FROM_UNION':
        case 'TYPE_REMOVED_FROM_INTERFACE':
        case 'VALUE_REMOVED_FROM_ENUM':
        case 'VALUE_ADDED_TO_ENUM':
        case 'TYPE_ADDED_TO_INTERFACE':
        case 'TYPE_DESCRIPTION_CHANGE':
        case 'ENUM_VALUE_DESCRIPTION_CHANGE':
          return 'THIS_CHANGED';
        case 'FIELD_ADDED':
        case 'FIELD_REMOVED':
        case 'FIELD_REMOVED_FROM_INPUT_OBJECT':
        case 'NULLABLE_INPUT_FIELD_ADDED':
        case 'NON_NULL_INPUT_FIELD_ADDED':
        case 'INPUT_FIELD_REMOVED':
        case 'FIELD_CHANGED_KIND':
        case 'FIELD_DEPRECATION_REMOVED':
        case 'FIELD_DEPRECATED_REASON_CHANGE':
        case 'FIELD_DEPRECATED':
        case 'FIELD_CHANGED_TYPE':
        case 'FIELD_ON_INPUT_OBJECT_CHANGED_TYPE':
        case 'ARG_ADDED':
        case 'ARG_REMOVED':
        case 'ARG_CHANGED_TYPE':
        case 'ARG_CHANGED_KIND':
        case 'ARG_CHANGED_TYPE_OPTIONAL_TO_REQUIRED':
        case 'ARG_DEFAULT_VALUE_CHANGE':
        case 'ARG_DESCRIPTION_CHANGE':
        case 'REQUIRED_ARG_ADDED':
        case 'OPTIONAL_ARG_ADDED':
        case 'NULLABLE_FIELD_ADDED_TO_INPUT_OBJECT':
        case 'OPTIONAL_FIELD_ADDED_TO_INPUT_OBJECT':
        case 'NON_NULLABLE_FIELD_ADDED_TO_INPUT_OBJECT':
        case 'REQUIRED_FIELD_ADDED_TO_INPUT_OBJECT':
        case 'FIELD_DESCRIPTION_CHANGE':
        case 'INPUT_OBJECT_FIELD_DEFAULT_VALUE_CHANGE':
        case 'INPUT_OBJECT_FIELD_DEFAULT_VALUE_REMOVED':
        case 'INPUT_OBJECT_FIELD_DEFAULT_VALUE_ADDED':
          return 'SOMETHING_INSIDE_THIS_CHANGED';
        default:
          return assertUnreachableOrReturnDefault(
            trimmedChangeCode as never,
            null,
          );
      }
    }
    case 'field': {
      switch (trimmedChangeCode) {
        case 'FIELD_ADDED':
        case 'NULLABLE_FIELD_ADDED_TO_INPUT_OBJECT':
        case 'OPTIONAL_FIELD_ADDED_TO_INPUT_OBJECT':
        case 'NON_NULLABLE_FIELD_ADDED_TO_INPUT_OBJECT':
        case 'REQUIRED_FIELD_ADDED_TO_INPUT_OBJECT':
          return 'ADDITION';
        case 'FIELD_REMOVED':
        case 'FIELD_REMOVED_FROM_INPUT_OBJECT':
          return 'REMOVAL';
        case 'FIELD_DEPRECATED':
          return 'DEPRECATION';
        case 'NULLABLE_INPUT_FIELD_ADDED':
        case 'NON_NULL_INPUT_FIELD_ADDED':
        case 'INPUT_FIELD_REMOVED':
        case 'FIELD_CHANGED_KIND':
        case 'FIELD_DEPRECATION_REMOVED':
        case 'FIELD_DEPRECATED_REASON_CHANGE':
        case 'FIELD_CHANGED_TYPE':
        case 'FIELD_ON_INPUT_OBJECT_CHANGED_TYPE':
        case 'FIELD_DESCRIPTION_CHANGE':
        case 'INPUT_OBJECT_FIELD_DEFAULT_VALUE_CHANGE':
        case 'INPUT_OBJECT_FIELD_DEFAULT_VALUE_REMOVED':
        case 'INPUT_OBJECT_FIELD_DEFAULT_VALUE_ADDED':
          return 'THIS_CHANGED';
        case 'ARG_ADDED':
        case 'ARG_CHANGED_TYPE':
        case 'REQUIRED_ARG_ADDED':
        case 'OPTIONAL_ARG_ADDED':
        case 'ARG_REMOVED':
        case 'ARG_CHANGED_KIND':
        case 'ARG_DEFAULT_VALUE_CHANGE':
        case 'ARG_DESCRIPTION_CHANGE':
        case 'ARG_CHANGED_TYPE_OPTIONAL_TO_REQUIRED':
          return 'SOMETHING_INSIDE_THIS_CHANGED';
        // these should return null because they don't apply / don't make sense for "field"
        case 'TYPE_ADDED_TO_UNION':
        case 'TYPE_REMOVED_FROM_UNION':
        case 'VALUE_ADDED_TO_ENUM':
        case 'TYPE_ADDED':
        case 'TYPE_REMOVED':
        case 'TYPE_DESCRIPTION_CHANGE':
        case 'ENUM_DEPRECATED':
        case 'ENUM_VALUE_ADDED':
        case 'INTERFACE_ADDED_TO_OBJECT':
        case 'ENUM_VALUE_REMOVED':
        case 'INTERFACE_REMOVED_FROM_OBJECT':
        case 'TYPE_CHANGED_KIND':
        case 'ENUM_DEPRECATION_REMOVED':
        case 'ENUM_DEPRECATED_REASON_CHANGE':
        case 'TYPE_REMOVED_FROM_INTERFACE':
        case 'VALUE_REMOVED_FROM_ENUM':
        case 'TYPE_ADDED_TO_INTERFACE':
        case 'ENUM_VALUE_DESCRIPTION_CHANGE':
          return null;
        default:
          return assertUnreachableOrReturnDefault(
            trimmedChangeCode as never,
            null,
          );
      }
    }
    case 'argument': {
      switch (trimmedChangeCode) {
        case 'ARG_ADDED':
        case 'REQUIRED_ARG_ADDED':
        case 'OPTIONAL_ARG_ADDED':
          return 'ADDITION';
        case 'ARG_REMOVED':
          return 'REMOVAL';
        case 'ARG_CHANGED_KIND':
        case 'ARG_CHANGED_TYPE':
        case 'ARG_DEFAULT_VALUE_CHANGE':
        case 'ARG_DESCRIPTION_CHANGE':
        case 'ARG_CHANGED_TYPE_OPTIONAL_TO_REQUIRED':
          return 'THIS_CHANGED';
        default:
          return assertUnreachableOrReturnDefault(
            trimmedChangeCode as never,
            null,
          );
      }
    }
    default:
      return assertUnreachable(typeOfThing);
  }
}
